import { Fragment } from "react";
import { DayCell } from "@/components/DayCell";
import type { OccupancyMap } from "@/lib/calendarOccupancy";
import { isWeekendOrHolidayBW } from "@/lib/holidaysBW";

const MONTHS_DE = [
  "Januar","Februar","März","April","Mai","Juni",
  "Juli","August","September","Oktober","November","Dezember",
];

const DOW_SHORT = ["S","M","D","M","D","F","S"];

const EMPTY = { morning: false, afternoon: false };

type Props = {
  year: number;
  month: number; // 0-based
  unitNames: string[];
  occupancy: OccupancyMap;
  cellWidth?: number;
  cellHeight?: number;
};

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function isoKey(year: number, month: number, day: number): string {
  return `${year}-${pad2(month + 1)}-${pad2(day)}`;
}

function formatDe(year: number, month: number, day: number): string {
  return `${pad2(day)}.${pad2(month + 1)}.${year}`;
}

function tooltipFor(date: string, state: { morning: boolean; afternoon: boolean }): string {
  const v = state.morning ? "belegt" : "frei";
  const n = state.afternoon ? "belegt" : "frei";
  const vSuffix = state.morning ? " bis 10:00" : "";
  const nSuffix = state.afternoon ? "" : " ab 15:00";
  return `${date} – vormittags: ${v}${vSuffix} / nachmittags: ${n}${nSuffix}`;
}

export function MonthStrip({
  year,
  month,
  unitNames,
  occupancy,
  cellHeight = 22,
}: Props) {
  const num = daysInMonth(year, month);
  const days = Array.from({ length: num }, (_, i) => i + 1);
  const labelWidth = 36;

  // Ein einziges Grid für Header- und Belegungszeilen: Alle Tagesspalten teilen
  // sich dieselben Tracks. Dadurch liegen Datum und Belegung auf jedem Gerät
  // pixelgenau übereinander – getrennte Flex-Container würden pro Zeile
  // unabhängig (subpixel-)gerundet und auf manchen Geräten auseinanderdriften.
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `${labelWidth}px repeat(${num}, minmax(0, 1fr))`,
      border: "1px solid var(--border-strong)",
      borderRadius: 8,
      background: "var(--bg-card)",
      overflow: "hidden",
      width: "100%",
    }}>
      {/* Monats-Header */}
      <div style={{
        gridColumn: "1 / -1",
        background: "var(--brand-700)",
        color: "var(--fg-inverse)",
        padding: "6px 12px",
        fontFamily: "var(--font-display)",
        fontSize: 16,
        letterSpacing: ".02em",
      }}>
        {MONTHS_DE[month]} {year}
      </div>

      {/* Tag-Header: leeres Label */}
      <div style={{
        height: 28,
        background: "var(--bg-subtle)",
        borderRight: "1px solid var(--border-strong)",
        boxSizing: "border-box",
      }} />
      {days.map(d => {
        const date = new Date(year, month, d);
        const we = isWeekendOrHolidayBW(date);
        const dow = date.getDay();
        return (
          <div
            key={`h-${d}`}
            style={{
              height: 28,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 9,
              fontWeight: 500,
              color: we ? "var(--accent)" : "var(--fg-muted)",
              background: we ? "color-mix(in oklab, var(--accent) 8%, transparent)" : "var(--bg-subtle)",
              borderRight: "1px solid var(--border-strong)",
              boxSizing: "border-box",
              lineHeight: 1.1,
            }}
          >
            <span style={{ fontSize: 8, opacity: 0.75 }}>{DOW_SHORT[dow]}</span>
            <span style={{ fontSize: 10 }}>{d}</span>
          </div>
        );
      })}

      {/* Einheit-Zeilen */}
      {unitNames.map(unit => {
        const unitMap = occupancy.get(unit);
        return (
          <Fragment key={unit}>
            <div style={{
              height: cellHeight,
              padding: "0 6px",
              display: "flex",
              alignItems: "center",
              fontSize: 12,
              fontWeight: 600,
              color: "var(--fg)",
              background: "var(--bg-subtle)",
              borderRight: "1px solid var(--border-strong)",
              borderTop: "1px solid var(--border-strong)",
              boxSizing: "border-box",
            }}>
              {unit}
            </div>
            {days.map(d => {
              const key = isoKey(year, month, d);
              const state = (unitMap && unitMap.get(key)) ?? EMPTY;
              const date = new Date(year, month, d);
              const we = isWeekendOrHolidayBW(date);
              return (
                <div
                  key={`${unit}-${d}`}
                  style={{
                    height: cellHeight,
                    borderRight: "1px solid var(--border-strong)",
                    borderTop: "1px solid var(--border-strong)",
                    background: we ? "color-mix(in oklab, var(--accent) 4%, transparent)" : "transparent",
                    boxSizing: "border-box",
                  }}
                >
                  <DayCell
                    state={state}
                    title={tooltipFor(formatDe(year, month, d), state)}
                  />
                </div>
              );
            })}
          </Fragment>
        );
      })}
    </div>
  );
}
